import {useCallback, useEffect, useMemo, useState} from 'react';

import {addMinutes} from 'date-fns';

import {getCushionTime} from 'Utils/apptAvailabilityUtils';

const defaultShouldRefresh = () => true;

/**
 * Custom hook to manage time-dependent refresh logic
 * @param {Object} options
 * @param {?Number} options.refreshInterval set an interval for periodic date refresh in ms
 * @param {?Number} options.refreshTimeout set a timeout for the next date refresh
 * @param {?Boolean} options.refreshOnFocus whether or not to refresh on window focus
 * @param {?Function} options.shouldRefresh function to decide whether latest Date value should cause a refresh
 * @return {Date}
 */
export function useDateRefresher(options={
  refreshInterval: undefined,
  refreshOnFocus: false,
  refreshTimeout: undefined,
  shouldRefresh: defaultShouldRefresh,
}) {
  const refreshInterval = options?.refreshInterval;
  const refreshOnFocus = options?.refreshOnFocus;
  const refreshTimeout = options?.refreshTimeout;
  const shouldRefresh = options?.shouldRefresh || defaultShouldRefresh;

  const [now, setNow] = useState(new Date());

  const updateNow = useCallback(() => {
    const now = new Date();
    console.log('checking update', now);
    if (shouldRefresh(now)) {
      console.log('shouldRefresh', now);
      setNow(now);
    }
  }, [shouldRefresh]);

  useEffect(() => {
    if (refreshInterval) {
      const interval = setInterval(updateNow, refreshInterval);
      return () => {
        clearInterval(interval);
      };
    }
  }, [refreshInterval, updateNow]);

  useEffect(() => {
    if (refreshTimeout) {
      console.log('set refresh timeout', refreshTimeout);
      const interval = setTimeout(updateNow, refreshTimeout);
      return () => {
        console.log('clear timeout', refreshTimeout);
        clearTimeout(interval);
      };
    }
  }, [refreshTimeout, updateNow]);

  useEffect(() => {
    if (refreshOnFocus) {
      window.addEventListener('focus', updateNow);
      return () => {
        window.removeEventListener('focus', updateNow);
      };
    }
  }, [refreshOnFocus, updateNow]);

  return now;
}

/**
 * custom hook to encapsulate time dependent refreshing logic and get the
 * first slot out of `availableTimes` that obeys the cushion cutoff parameter.
 *
 * refreshes returned value every time the next available slot fails the cushion requirement
 * @param {?Object} appt
 * @param {Array<Date>} availableTimes array of all non-busy times for a given day
 * @return {Date} the current cushion cutoff
 */
export function useCushionCutoff(appt, availableTimes) {
  const cushionMinutes = getCushionTime(appt);
  const cutoff = addMinutes(new Date(), cushionMinutes);
  const nextOpening = availableTimes.find((time) => cutoff < time);

  // Configures to refresh date when:
  // - next available time fails the cushion requirement
  // - window is re-focused
  const refreshParameters = useMemo(() => {
    // if there is no next opening, don't set a refresh timer
    const nextRefreshTimeout = nextOpening ? nextOpening - cutoff : undefined;
    return ({
      refreshOnFocus: true,
      refreshTimeout: nextRefreshTimeout,
      shouldRefresh: (currentTime) => {
        console.log('checking should refresh', currentTime, nextOpening);
        const nextCutoff = addMinutes(currentTime, cushionMinutes);
        if (nextOpening) {
          return nextCutoff > nextOpening;
        }
        return true;
      },
    });
  }, [nextOpening, cutoff, cushionMinutes]);

  const lastRefreshDate = useDateRefresher(refreshParameters);

  const cushionCutoff = addMinutes(lastRefreshDate, cushionMinutes);
  return cushionCutoff;
}
